import TotalManipulator from './TotalManipulator';

// COMBAK:
// - Use metafields to configure shipping protection.
// - Product id
// - Scale per currency
// (Lorenzo ~ 2023-05-31)

const { currency } = window.Shopify;

export const SHIPPING_PROTECTION_PRODUCT_ID = 4825104252971;

/**
 * We will increase the scale multiplier for the protection coverage
 * by 0.5% for every 50,000 JPY or 400 USD of the order total.
 * Example: Math.min(4.5, (order_total / scale + 0.2) * 0.5)
 */
export const SCALE_VALUE = currency.active === 'JPY' ? 50000 : 400;

export const fetchShippingProtectionProduct = async () => {
  const response = await fetch('/products/SSP.json');
  const { product } = await response.json();
  return product;
};

export const wantsProtection = () => {
  return document.querySelector('.shipping-protection .toggle-switch').classList.contains('checked');
};

export class ShippingProtection {
  constructor(cart) {
    this.cart = cart;
    this.shippingProtectionProduct = null;
    this.suggestedVariant = null;
    this.variantPrice = 0;
    this.setupListeners();
    this.init();
  }

  setCart(cart) {
    this.cart = cart;
    this.setSuggestedVariant();
  }

  setSuggestedVariant() {
    this.suggestedVariant = this.getSuggestedVariant();
    this.variantPrice = Number(this.suggestedVariant.price) * 100;
    this.modifyTotal(wantsProtection());
  }

  async init() {
    this.shippingProtectionProduct = await fetchShippingProtectionProduct();
    this.setSuggestedVariant();

    window.SolarisCart.checkoutHandler.registerPromise('shippingProtection', () => {
      return this.assureProtectionPromise();
    });

    this.setToggle(this.getProtectionSession());

    document.querySelector('.shipping-protection').classList.toggle('loading');
  }

  getSuggestedVariant() {
    const total = this.totalWithoutServices() / 100;
    const scale = Math.min(4.5, Math.floor(total / SCALE_VALUE + 0.2) * 0.5);
    const percentage = 2.5 + scale;

    return this.shippingProtectionProduct.variants.find((variant) => {
      const base = variant.final_line_price || Number(variant.price) * 100;
      const coverage = Math.ceil(base / percentage);
      return total <= coverage;
    });
  }

  modifyTotal(on) {
    const moneyEl = document.querySelector('.shipping-protection .description .money');
    TotalManipulator.updateElement(moneyEl, this.variantPrice);

    const currentCost = window.SolarisCart.totalManipulator.changes?.shippingProtection;

    if (on && (currentCost === 0 || currentCost !== this.variantPrice)) {
      if (currentCost > 0 && currentCost !== this.variantPrice) {
        window.SolarisCart.totalManipulator.clearChangesByKey('shippingProtection');
      }

      window.SolarisCart.totalManipulator.changeBy('shippingProtection', this.variantPrice);
    } else if (!on && currentCost === this.variantPrice) {
      window.SolarisCart.totalManipulator.changeBy('shippingProtection', -this.variantPrice);
    }
  }

  setupListeners() {
    document.querySelector('.shipping-protection .toggle-switch').addEventListener(
      'click',
      () => {
        this.setToggle(!wantsProtection());
      },
      { capture: true },
    );
  }

  setToggle(toggle) {
    this.setProtectionSession(toggle);

    const el = document.querySelector('.shipping-protection .toggle-switch');
    if (el) {
      el.classList.toggle('checked', toggle);
    }

    this.modifyTotal(toggle);
  }

  assureProtectionPromise() {
    return new Promise((resolve, reject) => {
      Shopify.getCart((cart) => {
        this.cart = cart;
        const planRequired = this.getSuggestedVariant();
        const updates = cart.items.reduce((acc, current) => {
          if (current.product_id === SHIPPING_PROTECTION_PRODUCT_ID) {
            acc[current.id] = 0;
          }

          // This is a fix for no cost items in the cart
          if (current.price === 0) {
            acc[current.id] = 0;
          }

          return acc;
        }, {});

        if (wantsProtection()) {
          updates[planRequired.id] = 1;
        }

        fetch('/cart/update.js', {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  setProtectionSession(value) {
    const data = {
      token: this.cart.token,
      value,
    };

    sessionStorage.setItem('protection', JSON.stringify(data));
  }

  getProtectionSession() {
    const protectionFromSession = sessionStorage.getItem('protection');

    if (protectionFromSession) {
      const pastSession = JSON.parse(protectionFromSession);
      if (pastSession && pastSession.token === this.cart.token) {
        return pastSession.value;
      }
    }

    this.setProtectionSession(true);

    return true;
  }

  totalWithoutServices() {
    return this.cart.total_price - window.SolarisCart.protectionsTotalCost();
  }
}
